import { apiRequest } from "./apiClient";

const currentTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Istanbul";
  } catch {
    return "Europe/Istanbul";
  }
};

const normalizeBirthdayDate = (value) => {
  const match = String(value || "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const normalizeBirthdayName = (value) => String(value || "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("tr-TR")
  .replace(/[^a-z0-9\u0131]+/g, " ")
  .trim()
  .replace(/\s+/g, " ");

export function findBirthdayOccurrence(occurrences, birthday = {}) {
  const candidates = Array.isArray(occurrences) ? occurrences.filter(Boolean) : [];
  if (!candidates.length) return null;

  const targetUid = String(birthday.celebrantUid || birthday.kisiUid || birthday.uid || "").trim();
  const targetUserId = String(birthday.celebrantUserId || birthday.kisiUserId || birthday.userId || "").trim();
  const targetDate = normalizeBirthdayDate(birthday.date || birthday.tarih);
  const targetName = normalizeBirthdayName(
    birthday.celebrantName || birthday.kisiAd || String(birthday.baslik || "").replace(/^\s*🎂\s*/, "")
  );
  const sameDate = targetDate
    ? candidates.filter((item) => normalizeBirthdayDate(item?.date) === targetDate)
    : candidates;
  const targetMonthDay = targetDate ? targetDate.slice(5) : "";
  const sameBirthday = targetMonthDay
    ? candidates.filter((item) => normalizeBirthdayDate(item?.date).slice(5) === targetMonthDay)
    : candidates;

  if (targetUserId) {
    const userIdMatch = sameDate.find(
      (item) => String(item?.celebrant?.userId || "").trim() === targetUserId
    );
    if (userIdMatch) return userIdMatch;
    const userIdBirthdayMatches = sameBirthday.filter(
      (item) => String(item?.celebrant?.userId || "").trim() === targetUserId
    );
    if (userIdBirthdayMatches.length === 1) return userIdBirthdayMatches[0];
  }

  if (targetUid) {
    const uidMatch = sameDate.find((item) => String(item?.celebrant?.uid || "").trim() === targetUid);
    if (uidMatch) return uidMatch;
    const uidBirthdayMatches = sameBirthday.filter(
      (item) => String(item?.celebrant?.uid || "").trim() === targetUid
    );
    if (uidBirthdayMatches.length === 1) return uidBirthdayMatches[0];
  }

  if (targetName) {
    const nameMatches = sameDate.filter(
      (item) => normalizeBirthdayName(item?.celebrant?.name) === targetName
    );
    if (nameMatches.length === 1) return nameMatches[0];
    const nameBirthdayMatches = sameBirthday.filter(
      (item) => normalizeBirthdayName(item?.celebrant?.name) === targetName
    );
    if (nameBirthdayMatches.length === 1) return nameBirthdayMatches[0];
  }

  // Aynı tarihte yalnız bir doğum günü varsa UID'nin farklı kaynaklarda farklı
  // biçimde tutulması kullanıcının detay ekranını açmasını engellememeli.
  if (sameDate.length === 1) return sameDate[0];
  return sameBirthday.length === 1 ? sameBirthday[0] : null;
}

export async function getUpcomingBirthdays(familyId, days = 45) {
  if (!familyId) return [];
  const params = new URLSearchParams({ days: String(days), timeZone: currentTimeZone() });
  const data = await apiRequest(`/birthdays/families/${encodeURIComponent(familyId)}/upcoming?${params.toString()}`, {
    skipAndroidLiteHeader: true,
  });
  return Array.isArray(data?.occurrences) ? data.occurrences : [];
}

export async function getBirthdayOccurrence(occurrenceId) {
  if (!occurrenceId) return null;
  const params = new URLSearchParams({ timeZone: currentTimeZone() });
  const data = await apiRequest(`/birthdays/occurrences/${encodeURIComponent(occurrenceId)}?${params.toString()}`, {
    skipAndroidLiteHeader: true,
  });
  return data?.occurrence || null;
}

export async function resolveBirthdayOccurrence(familyId, birthday = {}) {
  const celebrantRef = String(
    birthday.celebrantUserId || birthday.kisiUserId || birthday.userId ||
    birthday.celebrantUid || birthday.kisiUid || birthday.uid || ""
  ).trim();
  if (!familyId || !celebrantRef) return null;
  const date = normalizeBirthdayDate(birthday.date || birthday.tarih);
  const params = new URLSearchParams({ celebrantRef });
  if (date) params.set("date", date);
  params.set("timeZone", currentTimeZone());
  const data = await apiRequest(
    `/birthdays/families/${encodeURIComponent(familyId)}/resolve?${params.toString()}`,
    { skipAndroidLiteHeader: true }
  );
  return data?.occurrenceId ? data : null;
}

export async function claimBirthdayExperience() {
  return apiRequest("/birthdays/today/claim", {
    method: "POST",
    body: JSON.stringify({
      timeZone: currentTimeZone(),
    }),
    skipAndroidLiteHeader: true,
  });
}

export async function saveBirthdayWish(occurrenceId, { presetKey = "happy_birthday", message = "", update = false } = {}) {
  const suffix = update ? "/wishes/me" : "/wishes";
  return apiRequest(`/birthdays/occurrences/${encodeURIComponent(occurrenceId)}${suffix}`, {
    method: update ? "PATCH" : "POST",
    body: JSON.stringify({ presetKey, message, timeZone: currentTimeZone() }),
    skipAndroidLiteHeader: true,
  });
}

export async function deleteBirthdayWish(occurrenceId) {
  return apiRequest(`/birthdays/occurrences/${encodeURIComponent(occurrenceId)}/wishes/me`, {
    method: "DELETE",
    skipAndroidLiteHeader: true,
  });
}
